/** 将 Gateway PCM16 音频按事件采样率排进 Web Audio，并回传播放回执。 */

import { decodePcm16 } from "./audio";

interface PlaybackResponse {
  done: boolean;
  started: boolean;
  sources: Set<AudioBufferSourceNode>;
}

export class VoicePlayback {
  private cursor = 0;
  private readonly responses = new Map<string, PlaybackResponse>();

  constructor(
    private readonly context: AudioContext,
    private readonly sendReceipt: (
      type: "playback.started" | "playback.ended" | "playback.cancelled",
      responseId: string,
      reason?: string,
    ) => void,
  ) {}

  append(base64: string, sampleRate: number, responseId: string): void {
    if (!responseId || !Number.isInteger(sampleRate) || sampleRate <= 0) return;
    let response = this.responses.get(responseId);
    if (!response) {
      response = { done: false, started: false, sources: new Set() };
      this.responses.set(responseId, response);
    }
    try {
      const samples = decodePcm16(base64);
      const buffer = this.context.createBuffer(1, samples.length, sampleRate);
      const copy = new Float32Array(samples.length);
      copy.set(samples);
      buffer.copyToChannel(copy, 0);
      const source = this.context.createBufferSource();
      source.buffer = buffer;
      source.connect(this.context.destination);
      const start = Math.max(this.context.currentTime + 0.02, this.cursor);
      this.cursor = start + buffer.duration;
      response.sources.add(source);
      if (!response.started) {
        response.started = true;
        this.sendReceipt("playback.started", responseId);
      }
      source.addEventListener("ended", () => {
        response.sources.delete(source);
        this.finishIfReady(responseId);
      });
      source.start(start);
    } catch {
      this.sendReceipt("playback.cancelled", responseId, "playback_error");
      this.responses.delete(responseId);
    }
  }

  done(responseId: string): void {
    const response = this.responses.get(responseId);
    if (!response) {
      this.sendReceipt("playback.started", responseId);
      this.sendReceipt("playback.ended", responseId);
      return;
    }
    response.done = true;
    this.finishIfReady(responseId);
  }

  clear(reason = "interrupted"): void {
    for (const [responseId, response] of this.responses) {
      for (const source of response.sources) {
        try {
          source.stop();
        } catch {
          // AudioBufferSourceNode 可能已经结束。
        }
      }
      this.sendReceipt("playback.cancelled", responseId, reason);
    }
    this.responses.clear();
    this.cursor = 0;
  }

  dispose(): void {
    this.clear("connection_closed");
  }

  private finishIfReady(responseId: string): void {
    const response = this.responses.get(responseId);
    if (!response || !response.done || response.sources.size > 0) return;
    this.sendReceipt("playback.ended", responseId);
    this.responses.delete(responseId);
    if (this.responses.size === 0) this.cursor = 0;
  }
}
